export type HostSnapshot = {
  serverId: string | number;
  registeredTypes: string[];
  areaCount?: number;
  /** Live players across this host's instances. */
  memberCount?: number;
  clientCount?: number;
  maxClients?: number;
  /** Per-type concurrency caps this host declared `{ type: max }`. */
  typeCapacities?: { [type: string]: number };
  /** Per-type default instance policy this host declared `{ type: { emptyTtlMs?, maxClients? } }`. */
  typeDefaults?: { [type: string]: { emptyTtlMs?: number; maxClients?: number } };
  /** Host-level totals declared in config (max instances / max players). */
  limits?: { maxTotalAreas?: number; maxTotalClients?: number };
};

export type PlacementRequest = {
  type: string;
  requesterHostId?: string | number;
  gameId?: string;
};

/**
 * Decides which area-host process a new instance is placed on. Placement is a
 * pure control-plane policy — it never touches the data path.
 *
 * Admission and placement are CONFIG-DRIVEN — there is no per-type author hook.
 * (`onPlaceInstance` and its successor `onCreateInstanceRequest` were removed
 * in favor of this contract.) Admission is enforced by the master's host filter
 * (`Master.doCreateOrJoinArea`): per-type concurrency caps (`maxConcurrent` /
 * `typeCapacities`), host-level totals (`limits`), and per-type defaults
 * (`emptyTtlMs`, `maxClients`) all come from area-type registration + host
 * config. Placement among the admitted hosts is this policy — the master's
 * `placement` field (default `SameProcessPlacement`; swap in
 * `LeastLoadedPlacement` to spread across a fleet).
 */
export interface PlacementPolicy {
  choose(req: PlacementRequest, hosts: HostSnapshot[]): string | number | Promise<string | number>;
}

const capable = (hosts: HostSnapshot[], type: string) =>
  hosts.filter((h) => h.registeredTypes.includes(type));

/**
 * Picks the capable host currently running the fewest instances — the
 * spread-the-load policy for multi-host clusters.
 */
export class LeastLoadedPlacement implements PlacementPolicy {
  public choose(req: PlacementRequest, hosts: HostSnapshot[]): string | number {
    const eligible = capable(hosts, req.type);
    if (!eligible.length) {
      throw new Error(`No area-host advertises area type "${req.type}".`);
    }
    return eligible.reduce((best, h) =>
      (h.areaCount ?? 0) < (best.areaCount ?? 0) ? h : best,
    ).serverId;
  }
}

/**
 * Prefers co-locating the instance in the requester's own process (so all
 * dispatch stays on the in-process direct-call fast path); otherwise picks any
 * host that can run the type.
 */
export class SameProcessPlacement implements PlacementPolicy {
  public choose(req: PlacementRequest, hosts: HostSnapshot[]): string | number {
    const eligible = capable(hosts, req.type);
    if (!eligible.length) {
      throw new Error(`No area-host advertises area type "${req.type}".`);
    }
    if (req.requesterHostId !== undefined) {
      const local = eligible.find((h) => h.serverId === req.requesterHostId);
      if (local) {
        return local.serverId;
      }
    }
    return eligible[0].serverId;
  }
}
